import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  FormError,
  Input,
  LoadingState,
  PageHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  formatDateTime,
  useToast,
} from '@lawbee/ui';
import { CheckCircle2, Mail, MessageSquare, Send, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage, isApiError } from '../../../lib/api';
import { can, useMe } from '../../../lib/me';
import type { CaseUpdate } from '../../../lib/types';
import { UpdateHistoryItem } from './matter-comms';
import { UpdateTemplatesEditor } from './templates';

export function UpdatesPage() {
  const [tab, setTab] = useState('queue');
  const pending = useQuery({ queryKey: ['updates', 'pending_approval'], queryFn: () => api.get<{ updates: CaseUpdate[] }>('/api/updates?status=pending_approval') });
  const count = pending.data?.updates.length ?? 0;
  return (
    <div>
      <PageHeader title="Client updates" description="Review drafts before they reach clients, see what was sent, and edit the templates stage changes use." />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList aria-label="Client update sections">
          <TabsTrigger value="queue">
            Approval queue{count ? <span className="figures text-muted-foreground">{count}</span> : null}
          </TabsTrigger>
          <TabsTrigger value="sent">Sent</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
        </TabsList>
        <TabsContent value="queue">
          <ApprovalQueue />
        </TabsContent>
        <TabsContent value="sent">
          <SentList />
        </TabsContent>
        <TabsContent value="templates">
          <UpdateTemplatesEditor />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ApprovalQueue() {
  const me = useMe();
  const q = useQuery({ queryKey: ['updates', 'pending_approval'], queryFn: () => api.get<{ updates: CaseUpdate[] }>('/api/updates?status=pending_approval') });
  const canApprove = can(me, 'approve_updates');
  if (q.isLoading) return <LoadingState label="Loading drafts" />;
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const updates = q.data?.updates ?? [];
  if (updates.length === 0) {
    return <EmptyState icon={<CheckCircle2 />} title="Nothing waiting for approval" description="Drafted updates appear here when a matter reaches a stage that notifies the client." />;
  }
  return (
    <div className="space-y-4">
      {!canApprove ? <Alert variant="info">A lawyer or owner approves updates before they go out. You can review the drafts and cancel ones that are no longer needed.</Alert> : null}
      <ul className="space-y-4">
        {updates.map((u) => (
          <PendingCard key={u.id} update={u} canApprove={canApprove} />
        ))}
      </ul>
    </div>
  );
}

function PendingCard({ update, canApprove }: { update: CaseUpdate; canApprove: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [subject, setSubject] = useState(update.subject ?? '');
  const [body, setBody] = useState(update.body);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['updates'] }),
      qc.invalidateQueries({ queryKey: ['matter', update.matterId] }),
      qc.invalidateQueries({ queryKey: ['dashboard'] }),
    ]);
  const approve = useMutation({
    mutationFn: () => api.post<{ update: CaseUpdate }>(`/api/updates/${update.id}/approve`, { ...(update.channel === 'email' ? { subject: subject.trim() || null } : {}), body: body.trim() }),
    onSuccess: async () => {
      setInlineError(null);
      toast.success('Update sent');
      await invalidate();
    },
    onError: (err) => {
      if (isApiError(err, 'guardrail') || isApiError(err, 'undeliverable')) setInlineError(errorMessage(err));
      else toast.error('Could not send', errorMessage(err));
      if (isApiError(err, 'undeliverable')) void invalidate();
    },
  });
  const cancel = useMutation({
    mutationFn: () => api.post(`/api/updates/${update.id}/cancel`),
    onSuccess: async () => {
      toast.success('Draft cancelled');
      await invalidate();
    },
    onError: (err) => toast.error('Could not cancel', errorMessage(err)),
  });
  const id = `upd-${update.id}`;

  return (
    <li className="rounded-xl bg-card p-5 shadow-card">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Link to="/matters/$id" params={{ id: update.matterId }} className="font-semibold hover:underline">
            {update.matterNumber ? <span className="figures mr-1.5 text-muted-foreground">{update.matterNumber}</span> : null}
            {update.matterTitle ?? 'Matter'}
          </Link>
          {update.clientName ? <p className="text-[13px] text-muted-foreground">To {update.clientName}</p> : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">
            {update.channel === 'sms' ? <MessageSquare className="size-3" aria-hidden /> : <Mail className="size-3" aria-hidden />}
            {update.channel === 'sms' ? 'SMS' : 'Email'}
          </Badge>
          {update.aiDrafted ? (
            <Badge variant="primary">
              <Sparkles className="size-3" aria-hidden /> AI drafted
            </Badge>
          ) : null}
          <span className="figures text-[12px] text-muted-foreground">{formatDateTime(update.createdAt)}</span>
        </div>
      </div>

      {update.guardrailFlags.length > 0 ? (
        <Alert variant="warning" className="mb-4" title="Check the wording">
          This draft may read as legal advice: {update.guardrailFlags.join(', ')}. Rephrase it as a status update before sending.
        </Alert>
      ) : null}

      <div className="space-y-4">
        {update.channel === 'email' ? (
          <Field label="Subject" htmlFor={`${id}-subject`}>
            <Input id={`${id}-subject`} value={subject} readOnly={!canApprove} onChange={(e) => setSubject(e.target.value)} />
          </Field>
        ) : null}
        <Field label="Message" htmlFor={`${id}-body`} description={update.channel === 'sms' ? <span className="figures">{body.length} characters</span> : undefined}>
          <Textarea id={`${id}-body`} rows={update.channel === 'sms' ? 4 : 9} value={body} readOnly={!canApprove} onChange={(e) => setBody(e.target.value)} />
        </Field>
        {inlineError ? <FormError message={inlineError} /> : null}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" loading={cancel.isPending} disabled={approve.isPending} onClick={() => cancel.mutate()}>
            Cancel draft
          </Button>
          {canApprove ? (
            <Button loading={approve.isPending} disabled={!body.trim() || cancel.isPending} onClick={() => approve.mutate()}>
              <Send aria-hidden /> Approve and send
            </Button>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function SentList() {
  const q = useQuery({ queryKey: ['updates', 'sent'], queryFn: () => api.get<{ updates: CaseUpdate[] }>('/api/updates?status=sent') });
  if (q.isLoading) return <LoadingState label="Loading sent updates" />;
  if (q.error) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const updates = q.data?.updates ?? [];
  if (updates.length === 0) return <EmptyState icon={<Send />} title="No updates sent yet" description="Updates you approve or send directly are listed here." />;
  return (
    <ul className="space-y-3">
      {updates.map((u) => (
        <UpdateHistoryItem key={u.id} update={u} showMatter />
      ))}
    </ul>
  );
}
